import type React from "react";
import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as Accordion from "@radix-ui/react-accordion";
import * as Dialog from "@radix-ui/react-dialog";
import { IoClose } from "react-icons/io5";
import { PiCaretDownBold } from "react-icons/pi";
import { Button, Flex } from "@radix-ui/themes";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import GenresIcon from "@/assets/icons/Genres.svg?react";
import MoodIcon from "@/assets/icons/Mood.svg?react";
import KeyIcon from "@/assets/icons/Key.svg?react";
import BpmIcon from "@/assets/icons/BPM.svg?react";
import { cn } from "@/lib/utils";

import { useTracksControllerTracksListGenres } from "@/lib/lutely-api/gen/tracks/tracks";

import MobileBPMFilter from "./MobileBPMFilter";
import MobileGenreFilter from "./MobileGenreFilter";
import MobileKeyFilter from "./MobileKeyFilter";
import MobileMoodsFilter from "./MobileMoodsFilter";
import {
  MarketplaceSearch,
  MarketplaceFilterKey,
  MarketplaceSort,
  MarketplaceUpdates,
} from "../types";
import { SORT_OPTIONS } from "@/lib/marketplace-sort";

const BPM_DEFAULT_RANGE: [number, number] = [20, 240];

type DraftFilters = {
  genres: string[];
  moods: string[];
  keySignature: string | null;
  bpmRange: [number, number];
};

type MobileFiltersDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchParameters: MarketplaceSearch;
  onSetFilter: (updates: MarketplaceUpdates) => void;
  onRemoveFilter: (param: MarketplaceFilterKey) => void;
  sortOptions?: { value: MarketplaceSort; label: string }[];
};

export default function MobileFiltersDrawer({
  open,
  onOpenChange,
  searchParameters,
  onSetFilter,
  onRemoveFilter,
  sortOptions = SORT_OPTIONS,
}: MobileFiltersDrawerProps) {
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(() =>
    createDraftFilters(searchParameters),
  );

  useEffect(() => {
    if (open) {
      setDraftFilters(createDraftFilters(searchParameters));
    }
  }, [open, searchParameters]);

  const { data: genresData = [] } = useTracksControllerTracksListGenres();
  const genreIdSet = useMemo(
    () => new Set(genresData.map((genre) => genre.classificationId)),
    [genresData],
  );

  const summaries = useMemo(
    () => getSummaries(draftFilters, { genreIdSet }),
    [draftFilters, genreIdSet],
  );
  const bpmIsDefault = isRangeEqual(draftFilters.bpmRange, BPM_DEFAULT_RANGE);

  const filterActiveStates = {
    genre: draftFilters.genres.length > 0,
    mood: draftFilters.moods.length > 0,
    key: Boolean(draftFilters.keySignature),
    bpm: !bpmIsDefault,
  };

  const applyFilters = () => {
    const updates: Record<string, string> = {};
    const removals: MarketplaceFilterKey[] = [];

    if (draftFilters.genres.length > 0) {
      updates.genre = draftFilters.genres.join(", ");
    } else {
      removals.push("genre");
    }

    if (draftFilters.moods.length > 0) {
      updates.mood = draftFilters.moods.join(", ");
    } else {
      removals.push("mood");
    }

    if (draftFilters.keySignature) {
      updates.key = draftFilters.keySignature;
    } else {
      removals.push("key");
    }

    const isDefaultBpm =
      draftFilters.bpmRange[0] === BPM_DEFAULT_RANGE[0] &&
      draftFilters.bpmRange[1] === BPM_DEFAULT_RANGE[1];
    if (!isDefaultBpm) {
      updates.bpm = `${draftFilters.bpmRange[0]}-${draftFilters.bpmRange[1]}`;
    } else {
      removals.push("bpm");
    }

    if (Object.keys(updates).length > 0) {
      onSetFilter(updates);
    }

    removals.forEach((param) => onRemoveFilter(param));

    onOpenChange(false);
  };

  const handleReset = () => {
    setDraftFilters(createDraftFilters({}));
    (["genre", "mood", "key", "bpm"] as MarketplaceFilterKey[]).forEach(
      onRemoveFilter,
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 top-0 flex flex-col outline-none">
          <VisuallyHidden>
            <Dialog.Title>Filters</Dialog.Title>
          </VisuallyHidden>
          <div className="mt-auto flex max-h-[95vh] flex-col rounded-t-[24px] bg-black text-white shadow-[0_-20px_40px_rgba(0,0,0,0.6)]">
            <header className="flex items-center justify-between px-4 pt-4 pb-3">
              <div className="w-[34px]" aria-hidden />
              <span className="text-[14px] font-semibold text-white/70">
                Filters
              </span>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-white/10"
                >
                  <IoClose className="text-[20px]" />
                  <span className="sr-only">Close filters</span>
                </button>
              </Dialog.Close>
            </header>

            <div className="h-px w-full bg-gradient-to-r from-white/20 via-white/10 to-white/0" />

            <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4">
              {/* Sort section — immediate, no Apply needed */}
              <div className="mb-5">
                <p className="mb-2 text-[12px] font-semibold uppercase tracking-widest text-white/40">
                  Sort by
                </p>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((option) => {
                    const isActive =
                      (searchParameters.sort ?? "discover") === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          onSetFilter({
                            sort: option.value as MarketplaceSort,
                          });
                          onOpenChange(false);
                        }}
                        className={cn(
                          "rounded-full border px-4 py-1.5 text-[13px] font-semibold transition-colors",
                          isActive
                            ? "border-[#43DDA7] bg-[#43DDA7]/10 text-[#43DDA7]"
                            : "border-white/20 text-white/60 hover:border-white/40 hover:text-white",
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-4 h-px w-full bg-white/10" />

              <Accordion.Root type="multiple" className="flex flex-col gap-3">
                <MobileFilterSection
                  value="genre"
                  icon={<GenresIcon className="h-5 w-5" fill="currentColor" />}
                  label="Genre"
                  summary={summaries.genre}
                  isActive={filterActiveStates.genre}
                >
                  <MobileGenreFilter
                    value={draftFilters.genres}
                    onChange={(next) =>
                      setDraftFilters((prev) => ({ ...prev, genres: next }))
                    }
                  />
                </MobileFilterSection>

                <MobileFilterSection
                  value="mood"
                  icon={<MoodIcon className="h-5 w-5" fill="currentColor" />}
                  label="Moods"
                  summary={summaries.mood}
                  isActive={filterActiveStates.mood}
                >
                  <MobileMoodsFilter
                    value={draftFilters.moods}
                    onChange={(next) =>
                      setDraftFilters((prev) => ({ ...prev, moods: next }))
                    }
                  />
                </MobileFilterSection>

                <MobileFilterSection
                  value="key"
                  icon={<KeyIcon className="h-5 w-5" fill="currentColor" />}
                  label="Key"
                  summary={summaries.key}
                  isActive={filterActiveStates.key}
                >
                  <MobileKeyFilter
                    value={draftFilters.keySignature}
                    onChange={(next) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        keySignature: next,
                      }))
                    }
                  />
                </MobileFilterSection>

                <MobileFilterSection
                  value="bpm"
                  icon={<BpmIcon className="h-5 w-5" fill="currentColor" />}
                  label="BPM"
                  summary={summaries.bpm}
                  isActive={filterActiveStates.bpm}
                >
                  <MobileBPMFilter
                    value={draftFilters.bpmRange}
                    onChange={(next) =>
                      setDraftFilters((prev) => ({ ...prev, bpmRange: next }))
                    }
                  />
                </MobileFilterSection>
              </Accordion.Root>
            </div>

            <Flex className="items-center justify-between px-4 pb-6">
              <Button
                onClick={handleReset}
                variant="ghost"
                size="3"
                className="text-teal-500"
              >
                Reset
              </Button>
              <Button
                onClick={applyFilters}
                size="2"
                radius="full"
                className="rounded-full"
              >
                Apply
              </Button>
            </Flex>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type SectionProps = {
  value: string;
  icon: React.ReactNode;
  label: string;
  summary: string;
  children: React.ReactNode;
  isActive?: boolean;
};

type IconNodeProps = {
  className?: string;
  fill?: string;
};

function MobileFilterSection({
  value,
  icon,
  label,
  summary,
  children,
  isActive = false,
}: SectionProps) {
  const iconNode = isValidElement<IconNodeProps>(icon)
    ? cloneElement(icon, {
        className: cn(
          "h-5 w-5 transition-colors",
          isActive ? "text-[#43DDA7]" : "text-white",
          icon.props.className,
        ),
        fill: icon.props.fill ?? "currentColor",
      })
    : icon;

  return (
    <Accordion.Item value={value} className="border-b border-white/10">
      <Accordion.Header>
        <Accordion.Trigger className="group flex w-full items-center gap-4 px-4 py-4 text-left">
          <div className="flex items-center gap-3 w-[120px] shrink-0">
            {iconNode}
            <span
              className={cn(
                "text-[15px] font-semibold",
                isActive ? "text-[#43DDA7]" : "text-white",
              )}
            >
              {label}
            </span>
          </div>
          <span className="flex-1 text-[14px] text-[#a5abb3] whitespace-nowrap overflow-hidden text-ellipsis">
            {summary}
          </span>
          <PiCaretDownBold
            aria-hidden
            className="text-[18px] text-white transition-transform group-data-[state=open]:rotate-180"
          />
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className="px-4 pb-5 pt-0 data-[state=closed]:hidden">
        {children}
      </Accordion.Content>
    </Accordion.Item>
  );
}

function createDraftFilters(
  searchParameters: Record<string, unknown>,
): DraftFilters {
  const getString = (key: string) => {
    const candidate = searchParameters[key];
    return typeof candidate === "string" ? candidate : undefined;
  };

  const parseList = (value?: string) =>
    value
      ? value
          .split(", ")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

  const parseRange = (
    value: string | undefined,
    fallback: [number, number],
  ): [number, number] => {
    if (!value) {
      return fallback;
    }
    const [start, end] = value.split("-").map((part) => parseInt(part, 10));
    if (Number.isNaN(start) || Number.isNaN(end)) {
      return fallback;
    }
    return [start, end];
  };

  return {
    genres: parseList(getString("genre")),
    moods: parseList(getString("mood")),
    keySignature: getString("key") || null,
    bpmRange: parseRange(getString("bpm"), BPM_DEFAULT_RANGE),
  };
}

function isRangeEqual(a: [number, number], b: [number, number]) {
  return a[0] === b[0] && a[1] === b[1];
}

type SummaryContext = {
  genreIdSet?: Set<string>;
};

function getSummaries(draft: DraftFilters, context: SummaryContext = {}) {
  const genre = draft.genres.length
    ? formatGenreSummary(draft.genres, context.genreIdSet)
    : "All genres";
  const mood = draft.moods.length
    ? `${draft.moods.length} selected`
    : "All moods";
  const key = draft.keySignature ?? "Any key";
  const bpmDefault =
    draft.bpmRange[0] === BPM_DEFAULT_RANGE[0] &&
    draft.bpmRange[1] === BPM_DEFAULT_RANGE[1];
  const bpm = bpmDefault
    ? "Any BPM"
    : `${draft.bpmRange[0]} – ${draft.bpmRange[1]} BPM`;

  return { genre, mood, key, bpm };
}

function formatGenreSummary(
  selections: string[],
  genreIdSet: Set<string> | undefined,
) {
  if (!selections.length) {
    return "All genres";
  }

  if (!genreIdSet || genreIdSet.size === 0) {
    return `${selections.length} selected`;
  }

  let genreCount = 0;
  let subgenreCount = 0;

  selections.forEach((selection) => {
    if (genreIdSet.has(selection)) {
      genreCount += 1;
    } else {
      subgenreCount += 1;
    }
  });

  const parts: string[] = [];
  if (genreCount > 0) {
    parts.push(`${genreCount} genre${genreCount > 1 ? "s" : ""}`);
  }
  if (subgenreCount > 0) {
    parts.push(`${subgenreCount} subgenre${subgenreCount > 1 ? "s" : ""}`);
  }

  return parts.join(", ");
}
